/**
 * Chuẩn trạng thái Feedback EMS (v1) — nguồn duy nhất phía web.
 * Bản gốc phía server: packages/trpc/src/constants/feedback-status.ts (server mới là nơi kiểm tra quyền thật;
 * file này chỉ để UI ẩn/hiện nút theo cùng quy tắc).
 */
import type { FeedbackStatus } from "./feedback.schema";

export const FEEDBACK_RESOLUTION_NOTE_MAX = 400;
export const FEEDBACK_TYPES_REQUIRE_AFTER_IMAGE = ["BUG", "UI"];
export const FEEDBACK_PAGE_SIZE = 20;
export const FEEDBACK_MAX_IMAGES = 6;
/** Gợi ý ô Mô tả: bước làm / mong muốn / thực tế */
export const FEEDBACK_DESCRIPTION_HINT = "Bước làm: ...\nMong muốn: ...\nThực tế: ...";

export type FeedbackTransitionActor = "dev" | "leader";

export interface FeedbackTransitionRule {
  from: FeedbackStatus;
  to: FeedbackStatus;
  actor: FeedbackTransitionActor;
  requireNote: boolean;
  /** Nhãn nút hành động trên giao diện */
  actionLabel: string;
  kind: "start" | "resolve" | "reopen" | "close";
}

export const FEEDBACK_TRANSITIONS: FeedbackTransitionRule[] = [
  { from: "OPEN", to: "IN_PROGRESS", actor: "dev", requireNote: false, actionLabel: "Bắt đầu xử lý", kind: "start" },
  { from: "IN_PROGRESS", to: "RESOLVED", actor: "dev", requireNote: true, actionLabel: "Hoàn thành", kind: "resolve" },
  { from: "RESOLVED", to: "IN_PROGRESS", actor: "dev", requireNote: true, actionLabel: "Mở lại", kind: "reopen" },
  { from: "RESOLVED", to: "CLOSED", actor: "leader", requireNote: false, actionLabel: "Đóng phiếu", kind: "close" },
  { from: "CLOSED", to: "IN_PROGRESS", actor: "leader", requireNote: true, actionLabel: "Mở lại", kind: "reopen" },
];

export function findFeedbackTransition(from?: string | null, to?: string | null) {
  return FEEDBACK_TRANSITIONS.find((t) => t.from === from && t.to === to);
}

interface FeedbackUserLike {
  role?: string | null;
  permissions?: unknown;
}

function parsePermissions(raw: unknown): Record<string, any> {
  let perms: any = raw || {};
  if (typeof perms === "string") {
    try {
      perms = JSON.parse(perms);
    } catch {
      perms = {};
    }
  }
  if (Array.isArray(perms)) {
    if (perms.includes("*")) return { "*": { actions: ["*"] } };
    const out: Record<string, any> = {};
    for (const p of perms) {
      if (typeof p !== "string") continue;
      const [mod, act] = p.split(".");
      if (!mod || !act) continue;
      out[mod] = out[mod] || { actions: [] };
      out[mod].actions.push(act);
    }
    return out;
  }
  return typeof perms === "object" && perms ? perms : {};
}

export function isFeedbackAdmin(user?: FeedbackUserLike | null): boolean {
  if (!user) return false;
  const role = (user.role || "").toUpperCase();
  return role === "ADMIN" || role === "SUPERADMIN" || Boolean(parsePermissions(user.permissions)["*"]);
}

export function canProcessFeedback(user?: FeedbackUserLike | null): boolean {
  if (isFeedbackAdmin(user)) return true;
  const perms = parsePermissions(user?.permissions);
  const entry = perms.feedback || perms.feedbacks;
  const actions: string[] = Array.isArray(entry?.actions) ? entry.actions : [];
  return actions.includes("*") || actions.includes("approve");
}

export function canCloseFeedback(user?: FeedbackUserLike | null): boolean {
  if (canProcessFeedback(user)) return true;
  const role = (user?.role || "").toUpperCase();
  return role === "DIRECTOR" || role === "MANAGER";
}

/** Các bước chuyển hợp lệ từ trạng thái hiện tại mà người dùng được phép làm */
export function getAllowedFeedbackTransitions(
  status: string | null | undefined,
  user?: FeedbackUserLike | null,
): FeedbackTransitionRule[] {
  return FEEDBACK_TRANSITIONS.filter(
    (t) =>
      t.from === status &&
      (t.actor === "leader" ? canCloseFeedback(user) : canProcessFeedback(user)),
  );
}
